(function (root, factory) {
  const policy = factory();
  if (typeof module === 'object' && module.exports) module.exports = policy;
  if (root) root.SmxGiftPolicy = policy;
})(typeof window === 'undefined' ? null : window, function () {
  function failure(code) {
    const error = new Error(code);
    error.code = code;
    return error;
  }

  function create({ config, request }) {
    const giftIds = config.giftVariantIds.map(String);
    const identityFields = ['offer_id', 'bundle_id', 'config_id'];
    const metadata = { ...config.metadata };
    const maxMutations = 24;

    async function send(path, body) {
      let response;
      try {
        response = await request(path, body);
      } catch (error) {
        if (error && typeof error === 'object') {
          if (!error.code) error.code = 'gift_request_failed';
          throw error;
        }
        throw failure('gift_request_failed');
      }
      if (!response || response.errors || Number(response.status) >= 400) {
        throw failure('gift_request_failed');
      }
      return response;
    }

    function variant(line) {
      return String(line.variant_id ?? line.id);
    }

    function eligible(line) {
      return variant(line) === String(config.variantId) &&
        String(line.selling_plan_allocation?.selling_plan?.id) === String(config.planId);
    }

    function properties(line) {
      if (line.properties == null) return {};
      if (typeof line.properties !== 'object' || Array.isArray(line.properties)) {
        throw failure('gift_invalid_cart');
      }
      return line.properties;
    }

    function moon(line) {
      const value = properties(line).__moonbundle;
      if (value == null || value === '') return null;
      try {
        const result = JSON.parse(value);
        return result && typeof result === 'object' && !Array.isArray(result) ? result : false;
      } catch (_) {
        return false;
      }
    }

    function sameOffer(value) {
      return value && identityFields.every((field) => String(value[field]) === String(metadata[field]));
    }

    function promotionalGift(line) {
      const value = moon(line);
      return giftIds.includes(variant(line)) && !line.selling_plan_allocation && !line.selling_plan &&
        sameOffer(value) && value.type === 'gift';
    }

    function assertMainMetadata(line) {
      const value = moon(line);
      if (value === null) return null;
      if (!sameOffer(value) || value.type !== 'bundle' ||
          String(value.main_product_id) !== String(metadata.main_product_id) ||
          String(value.slot_id) !== String(config.slotId)) {
        throw failure('gift_conflicting_offer');
      }
      return value;
    }

    async function reconcile() {
      let changed = false;
      let mutations = 0;
      let currentSnapshot;
      let pendingSnapshot;

      async function mutate(path, body) {
        if (mutations >= maxMutations) throw failure('gift_repair_limit');
        await send(path, body);
        mutations += 1;
        changed = true;
        pendingSnapshot = currentSnapshot;
      }

      async function change(line, quantity, nextProperties) {
        if (typeof line.key !== 'string' || !line.key) throw failure('gift_invalid_cart');
        const body = { id: line.key, quantity };
        if (nextProperties) body.properties = nextProperties;
        await mutate('cart/change.js', body);
      }

      while (true) {
        const cart = await send('cart.js');
        if (!Array.isArray(cart.items) || cart.items.some((line) =>
          !Number.isInteger(line.quantity) || line.quantity <= 0)) {
          throw failure('gift_invalid_cart');
        }
        currentSnapshot = JSON.stringify(cart.items);
        if (pendingSnapshot === currentSnapshot) throw failure('gift_no_progress');
        pendingSnapshot = undefined;

        const mains = cart.items.filter(eligible);
        const gifts = cart.items.filter(promotionalGift);
        if (!mains.length) {
          if (!gifts.length) return { cart, changed, eligible: false };
          await change(gifts[0], 0);
          continue;
        }

        const mainMetadata = mains.map(assertMainMetadata);
        const missingMetadataIndex = mainMetadata.findIndex((value) => value === null);
        if (missingMetadataIndex !== -1) {
          const main = mains[missingMetadataIndex];
          await change(main, main.quantity, {
            ...properties(main),
            __moonbundle: JSON.stringify({ ...metadata, type: 'bundle', slot_id: config.slotId }),
          });
          continue;
        }

        let adjusted = false;
        for (const id of giftIds) {
          const matching = gifts.filter((line) => variant(line) === id);
          const keeper = matching.find((line) => line.quantity === 1 && line.final_line_price === 0) || matching[0];
          const duplicate = matching.find((line) => line !== keeper);
          if (duplicate) {
            await change(duplicate, 0);
            adjusted = true;
            break;
          }
          if (keeper && keeper.quantity !== 1) {
            await change(keeper, 1);
            adjusted = true;
            break;
          }
        }
        if (adjusted) continue;

        const missingIds = giftIds.filter((id) => !gifts.some((line) => variant(line) === id));
        if (missingIds.length) {
          const giftMetadata = {
            ...metadata,
            market_added: mainMetadata[0].market_added || metadata.market_added,
            type: 'gift',
          };
          await mutate('cart/add.js', {
            items: missingIds.map((id) => ({
              id: Number(id),
              quantity: 1,
              properties: { __moonbundle: JSON.stringify(giftMetadata) },
            })),
          });
          continue;
        }

        if (gifts.some((line) => line.final_line_price !== 0)) throw failure('gift_not_free');
        return { cart, changed, eligible: true };
      }
    }

    return { reconcile };
  }

  return { create };
});
